import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { MarginProps, margin } from 'styled-system'

import { Icon, IconButton, PopOver, PopOverItemType } from 'components'
import { PopOverProps } from 'components/PopOver/types'
import Spinner from 'components/Spinner'
import { ThemeColor } from 'utils/colors'

enum TargetSize {
  medium = 16,
  small = 14,
  xsmall = 12,
}

type MoreOptionsProps = MarginProps & {
  align?: 'left' | 'right'
  children?: JSX.Element
  customMenuList?: React.ReactNode
  isDisabled?: boolean
  isLoading?: boolean
  isPortaled?: boolean
  isVisible?: boolean
  onSelect?: (value?: PopOverItemType) => void
  onToggleMenu?: PopOverProps['onToggleMenu']
  options?: PopOverItemType[]
  showTargetBg?: boolean
  showTargetBorder?: boolean
  targetSize?: keyof typeof TargetSize
  value?: PopOverItemType
  icon?: string | string[]
  iconButton?: boolean
  iconButtonWidth?: number
  iconButtonHeight?: number
  targetColor?: ThemeColor
}

// TODO : add grouped options

const MoreOptionsPopOver = ({
  align = 'left',
  children,
  customMenuList,
  isDisabled,
  isLoading,
  isPortaled = true,
  isVisible = true,
  onSelect,
  options,
  showTargetBg = true,
  targetColor = 'gray5',
  showTargetBorder = true,
  targetSize = 'medium',
  value,
  icon = 'ellipsis-v',
  iconButton = false,
  iconButtonWidth = 40,
  iconButtonHeight = 40,
  ...props
}: MoreOptionsProps) => {
  const [openMenu, setOpenMenu] = useState(false)
  const hasOptions = customMenuList || (options && options.length > 0)

  const component = isLoading ? (
    <Spinner />
  ) : iconButton ? (
    <IconButton
      {...props}
      icon="ellipsis-v"
      iconSize={TargetSize[targetSize]}
      onClick={() => setOpenMenu(!openMenu)}
      width={iconButtonWidth || 40}
      height={iconButtonHeight || 40}
      variant="outline"
    />
  ) : (
    <MainContainer {...props}>
      <Container
        active={openMenu}
        onClick={() => setOpenMenu(!openMenu)}
        showBg={showTargetBg}
        showBorder={showTargetBorder}
        size={TargetSize[targetSize]}
      >
        {hasOptions &&
          (children ? (
            children
          ) : (
            <Icon fontSize={TargetSize[targetSize]} icon={icon} color={openMenu ? 'blue1' : targetColor} />
          ))}
      </Container>
    </MainContainer>
  )

  if (isVisible) {
    return hasOptions ? (
      <PopOver
        customMenuList={customMenuList}
        isDisabled={isDisabled}
        onChange={onSelect}
        onToggleMenu={setOpenMenu}
        options={options}
        placement={align === 'right' ? 'bottom-end' : 'bottom-start'}
        value={value}
        highlighted
        isPortaled={isPortaled}
      >
        {component}
      </PopOver>
    ) : (
      component
    )
  }

  return null
}

const Container = styled.div<{ active?: boolean; showBg: boolean; showBorder: boolean; size: number }>`
  box-sizing: border-box;
  align-items: center;
  cursor: pointer;
  display: flex;
  min-height: ${(p) => p.size * 2}px;
  justify-content: space-evenly;
  min-width: ${(p) => p.size * 2}px;
  user-select: none;
  ${(p) =>
    p.showBg &&
    css`
      background-color: ${p.active ? p.theme.colors.blue5 : p.theme.colors.transparent};
    `}

  ${(p) =>
    p.showBorder &&
    css`
      border: solid 1px ${p.active ? p.theme.colors.blue2 : 'transparent'};
      border-radius: 4px;
    `}
`

const MainContainer = styled.div<MarginProps>`
  position: relative;
  ${margin}
`

export default MoreOptionsPopOver
